<template>
  <div class="config-view">
    <ConfigTopbar :park="park" :active-label="activeLabel" :pending-subsections="pendingSubsectionLabels" />

    <div v-if="loading" class="config-loading">
      <span class="mdi mdi-loading config-loading__spinner"></span>
      <span class="config-loading__text">Cargando configuración...</span>
    </div>

    <div v-else class="config-view__body">
      <nav class="config-menu">
        <button
          v-for="s in visibleSubsections"
          :key="s.key"
          class="config-menu__item"
          :class="{ 'config-menu__item--active': activeSubsection === s.key }"
          @click="onSelectSubsection(s.key)"
        >
          <span class="mdi" :class="s.icon"></span>
          {{ s.label }}
          <span v-if="pendingSet.has(s.key)" class="config-menu__pending-dot"></span>
        </button>
      </nav>

        <div class="config-content">
            <ConfigParkSection v-if="activeSubsection === 'parque' && park" :park-id="park.id" @dirty-change="(isDirty) => isDirty ? markPending('parque') : clearPending('parque')" />
            <ConfigBillingSection v-else-if="activeSubsection === 'cobranza' && park" :park-id="park.id" @dirty-change="(isDirty) => isDirty ? markPending('cobranza') : clearPending('cobranza')" />
            <ConfigServicesSection v-else-if="activeSubsection === 'servicios' && park" :park-id="park.id" @dirty-change="(isDirty) => isDirty ? markPending('servicios') : clearPending('servicios')" />
            <ConfigTechniciansSection v-else-if="activeSubsection === 'tecnicos' && park" :park-id="park.id" @dirty-change="(isDirty) => isDirty ? markPending('tecnicos') : clearPending('tecnicos')" />
            <ConfigNotificationsSection v-else-if="activeSubsection === 'notificaciones' && park" :park-id="park.id" @dirty-change="(isDirty) => isDirty ? markPending('notificaciones') : clearPending('notificaciones')" />
            <ConfigPlanSection v-else-if="activeSubsection === 'plan' && park && isAdmin" :park-id="park.id" />
            <ConfigDataSection v-else-if="activeSubsection === 'datos' && park && isAdmin" :park-id="park.id" />
            <ConfigUsersSection v-else-if="activeSubsection === 'usuarios' && park" :park-id="park.id" :park-name="park.name" @dirty-change="(isDirty) => isDirty ? markPending('usuarios') : clearPending('usuarios')" />
        </div>
      </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import ConfigTopbar from '@/components/Configuracion/ConfigTopbar.vue'
import { getParks, getParkById } from '@/services/parks'
import ConfigParkSection from '@/components/Configuracion/ConfigParkSection.vue'
import ConfigBillingSection from '@/components/Configuracion/ConfigBillingSection.vue'
import ConfigServicesSection from '@/components/Configuracion/ConfigServicesSection.vue'
import ConfigTechniciansSection from '@/components/Configuracion/ConfigTechniciansSection.vue'
import ConfigNotificationsSection from '@/components/Configuracion/ConfigNotificationsSection.vue'
import ConfigUsersSection from '@/components/Configuracion/ConfigUsersSection.vue'
import ConfigPlanSection from '@/components/Configuracion/ConfigPlanSection.vue'
import ConfigDataSection from '@/components/Configuracion/ConfigDataSection.vue'
import { useAuthStore } from '@/services/authStore'


const SUBSECTIONS = [
  { key: 'parque', label: 'Parque', icon: 'mdi-domain' },
  { key: 'cobranza', label: 'Cobranza', icon: 'mdi-cash-multiple' },
  { key: 'servicios', label: 'Servicios', icon: 'mdi-fire-hydrant' },
  { key: 'tecnicos', label: 'Técnicos', icon: 'mdi-account-hard-hat' },
  { key: 'usuarios', label: 'Usuarios y permisos', icon: 'mdi-account-key-outline' },
  { key: 'notificaciones', label: 'Notificaciones', icon: 'mdi-bell-outline' },
  // Solo para el administrador: es la relación comercial del parque con Parkos.
  { key: 'plan', label: 'Plan y facturación', icon: 'mdi-credit-card-outline', adminOnly: true },
  // Solo para el administrador: la copia incluye finanzas y datos de los inquilinos.
  { key: 'datos', label: 'Exportar datos', icon: 'mdi-database-export-outline', adminOnly: true },
]

const route = useRoute()
const { state: authState } = useAuthStore()
const isAdmin = computed(() => authState.user?.role === 'ADMIN')
const visibleSubsections = computed(() => SUBSECTIONS.filter((s) => !s.adminOnly || isAdmin.value))

const park = ref(null)
const loading = ref(true)
const activeSubsection = ref('parque')
const pendingSet = ref(new Set())

function applySectionFromQuery() {
  const section = route.query.section
  if (section && visibleSubsections.value.some((s) => s.key === section)) {
    activeSubsection.value = section
  }
}

onMounted(async () => {
  applySectionFromQuery()
  try {
    const parks = await getParks()
    if (parks[0]) park.value = await getParkById(parks[0].id)
  } finally {
    loading.value = false
  }
})

watch(() => route.query.section, applySectionFromQuery)

const activeLabel = computed(() => SUBSECTIONS.find((s) => s.key === activeSubsection.value)?.label || '')
const pendingSubsectionLabels = computed(() => SUBSECTIONS.filter((s) => pendingSet.value.has(s.key)).map((s) => s.label))

function onSelectSubsection(key) {
  activeSubsection.value = key
}

// TODO: cada subsección real (cuando se construya) llamará a estas funciones
// para marcar/desmarcar cambios pendientes en el indicador del topbar.
function markPending(key) {
  pendingSet.value = new Set(pendingSet.value).add(key)
}
function clearPending(key) {
  const next = new Set(pendingSet.value)
  next.delete(key)
  pendingSet.value = next
}

defineExpose({ markPending, clearPending })
</script>

<style scoped>
.config-view { display: flex; flex-direction: column; flex: 1; min-height: 0; }
.config-loading { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.75rem; color: var(--text-secondary); }
.config-loading__spinner { font-size: 1.8rem; animation: config-spin 0.8s linear infinite; }
.config-loading__text { font-size: 0.85rem; font-family: var(--font-body); }
@keyframes config-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.config-view__body { display: flex; flex: 1; min-height: 0; overflow: hidden; }

.config-menu {
  width: 200px;
  flex-shrink: 0;
  background: var(--bg-surface-1);
  border-right: 1px solid var(--border-active);
  padding: 1rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  overflow-y: auto;
}
.config-menu::-webkit-scrollbar { width: 6px; }
.config-menu::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 3px; }
.config-menu::-webkit-scrollbar-track { background: transparent; }

.config-menu__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  border-radius: 6px;
  padding: 0.55rem 0.65rem;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--text-content);
  cursor: pointer;
  text-align: left;
  position: relative;
}

.config-menu__item:hover { background: #f8fafc; }
.config-menu__item--active { background: #eff6ff; color: #1e3a5f; font-weight: 500; }
.config-menu__item .mdi { font-size: 16px; color: #94a3b8; }
.config-menu__item--active .mdi { color: #3b82f6; }

.config-menu__pending-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f0a500;
  margin-left: auto;
  flex-shrink: 0;
}

.config-content { flex: 1; min-width: 0; min-height: 0; overflow-y: auto; padding: 1.5rem; }
.config-content::-webkit-scrollbar { width: 6px; }
.config-content::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 3px; }
.config-content::-webkit-scrollbar-track { background: transparent; }

.config-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 100%;
  color: #94a3b8;
}

.config-placeholder__icon { font-size: 32px; }
.config-placeholder__text { font-family: var(--font-body); font-size: 13px; }
</style>